import { Link, useParams } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';

function formatViews(views: number): string {
  if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
  return views.toString();
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleById(slug) : undefined;

  if (!article) {
    return (
      <>
        <SEO title="文章未找到" description="抱歉，您访问的内容不存在。" />
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading text-foreground-900 mb-2">内容未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = getRelatedArticles(article);
  const canonicalUrl = `https://huangguavideo.com/article/${article.id}`;

  return (
    <>
      <SEO
        title={article.title}
        description={article.description}
        keywords={article.tags.join(',')}
        canonicalUrl={canonicalUrl}
        ogType="article"
        ogImage={article.thumbnail}
      />
      <SchemaOrg type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://huangguavideo.com' },
          { '@type': 'ListItem', position: 2, name: article.categoryName, item: `https://huangguavideo.com/category/${article.category}` },
          { '@type': 'ListItem', position: 3, name: article.title, item: canonicalUrl },
        ],
      }} />
      <SchemaOrg type="Article" data={{
        headline: article.title,
        description: article.description,
        image: article.thumbnail,
        datePublished: article.date,
        author: { '@type': 'Person', name: article.author },
        publisher: { '@type': 'Organization', name: '黄瓜视频', logo: { '@type': 'ImageObject', url: 'https://huangguavideo.com/logo.png' } },
        url: canonicalUrl,
        mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
      }} />

      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <div className="max-w-4xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.categoryName, href: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <article className="px-4 md:px-6 pb-16">
            <div className="mb-8">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <Link
                  to={`/category/${article.category}`}
                  className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium hover:bg-accent-200 transition-colors"
                >
                  {article.categoryName}
                </Link>
                {article.tags.slice(0, 3).map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full bg-background-100 text-foreground-500 text-xs">
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="text-xl md:text-3xl font-heading text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <div className="flex items-center gap-2">
                  <img
                    src={article.authorAvatar}
                    alt={article.author}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                  <span className="font-medium text-foreground-700">{article.author}</span>
                </div>
                <span>{article.date}</span>
                <span className="flex items-center gap-1">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-eye-line"></i>
                  </span>
                  {formatViews(article.views)} 次播放
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-thumb-up-line"></i>
                  </span>
                  {formatViews(article.likes)}
                </span>
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden mb-10">
              <img
                src={article.thumbnail}
                alt={article.title}
                title={article.title}
                className="w-full aspect-video object-cover"
              />
            </div>

            <div
              className="prose prose-lg max-w-none prose-headings:font-heading prose-headings:text-foreground-900 prose-p:text-foreground-700 prose-p:leading-relaxed prose-li:text-foreground-700 prose-strong:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 text-sm">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="px-4 md:px-6 pb-16">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-6">相关推荐</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.id}`}
                    className="group bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden hover:-translate-y-1 transition-all duration-300"
                  >
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={rel.thumbnail}
                        alt={rel.title}
                        title={rel.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading text-foreground-900 text-sm line-clamp-2 group-hover:text-primary-500 transition-colors">
                        {rel.title}
                      </h3>
                      <div className="flex items-center gap-3 mt-2 text-xs text-foreground-400">
                        <span className="flex items-center gap-1">
                          <span className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className="ri-eye-line"></i>
                          </span>
                          {formatViews(rel.views)}
                        </span>
                        <span>{rel.date}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}